import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type UnhideInspectionTemplateMutation,
  type UnhideInspectionTemplateMutationVariables,
} from 'graphql-query-contracts';
import { unhideInspectionTemplateMutation } from 'graphql-queries';

import { UnhideInspectionTemplateError } from './errors/UnhideInspectionTemplateError';

export type UnhideInspectionTemplateParams = {
  channelId: string;
  templateId: string;
};

export type UnhideInspectionTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseUnhideInspectionTemplateResult = {
  unhideInspectionTemplate: (
    params: UnhideInspectionTemplateParams
  ) => Promise<UnhideInspectionTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: UnhideInspectionTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new UnhideInspectionTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new UnhideInspectionTemplateError('templateId is required');
  }
}

export const useUnhideInspectionTemplate =
  (): UseUnhideInspectionTemplateResult => {
    const [unhideTemplate, { loading }] = useMutation<
      UnhideInspectionTemplateMutation,
      UnhideInspectionTemplateMutationVariables
    >(unhideInspectionTemplateMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const unhideInspectionTemplate = useCallback(
      async (
        params: UnhideInspectionTemplateParams
      ): Promise<UnhideInspectionTemplateResult> => {
        try {
          setError(undefined);

          validateParams(params);

          const variables: UnhideInspectionTemplateMutationVariables = {
            input: {
              channelId: params.channelId,
              templateId: params.templateId,
            },
          };

          const mutationResult = await unhideTemplate({ variables });

          const payload = mutationResult.data?.unhideInspectionTemplate;

          if (!payload) {
            throw new UnhideInspectionTemplateError();
          }

          if (!payload.success) {
            throw new UnhideInspectionTemplateError(
              'Unhide inspection template did not succeed'
            );
          }

          return {
            success: payload.success,
            newVersion: payload.newVersion,
          };
        } catch (err) {
          const wrappedError =
            err instanceof UnhideInspectionTemplateError
              ? err
              : new UnhideInspectionTemplateError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to unhide inspection template',
                  { cause: err }
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [unhideTemplate]
    );

    return {
      unhideInspectionTemplate,
      loading,
      error,
    };
  };
